import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { themeFor } from '@lubehub/theme';
import { createContext, use, useMemo, useState } from 'react';
import { cssVariables } from './cssVariables.js';
import { arabicLabels } from './labels.js';
const ThemeContext = createContext(null);
/**
 * Publishes the design tokens to the CSS below it.
 *
 * The tokens are set as inline custom properties on a wrapping element rather
 * than compiled into a stylesheet, for one reason worth stating: there is then
 * no generated artefact that can drift from `@lubehub/theme`. The values in the
 * DOM are read from the token module at render time, so a stale build cannot
 * produce a page styled with last week's palette.
 *
 * `dir` is set here too. Every component in this package uses CSS logical
 * properties (`margin-inline-start`, `inset-inline-end`, …) rather than
 * left/right, so setting the direction once at the root is genuinely all that
 * mirroring the interface takes.
 */
export function ThemeProvider({ children, name = 'light', direction = 'rtl', className, labels = arabicLabels, }) {
    // State, not a ref: overlays that mount before the layer must re-render
    // once it exists, or the first dialog of a session renders in place.
    const [layer, setLayer] = useState(null);
    const value = useMemo(() => ({ theme: themeFor(name), name, direction, labels, layer }), [name, direction, labels, layer]);
    const style = useMemo(() => cssVariables(value.theme), [value.theme]);
    return (_jsx(ThemeContext, { value: value, children: _jsxs("div", { className: ['lh-root', className].filter(Boolean).join(' '), dir: direction, "data-theme": name, style: style, children: [children, _jsx("div", { ref: setLayer, className: "lh-layer", "data-lh-layer": "" })] }) }));
}
/**
 * The active theme.
 *
 * Throws rather than falling back to light: a component rendering outside the
 * provider has no tokens on its ancestors either, so it would render unstyled.
 * Failing loudly at the first render beats shipping a page of invisible text.
 */
export function useTheme() {
    const value = use(ThemeContext);
    if (!value) {
        throw new Error('useTheme must be used inside a <ThemeProvider>.');
    }
    return value;
}
/**
 * The component labels, or the Arabic defaults outside a provider.
 *
 * Unlike {@link useTheme} this does not throw: a label is not styling, and a
 * component rendered alone in a unit test should still say something sensible.
 */
export function useUiLabels() {
    return use(ThemeContext)?.labels ?? arabicLabels;
}
/**
 * Where an overlay renders:
 *
 * - an element — portal into it;
 * - `null` — inside a provider whose layer has not mounted yet (the first
 *   commit): render nothing for that one frame. Rendering in place and then
 *   moving into the layer would remount the overlay and drop its focus;
 * - `undefined` — outside any provider: render in place.
 */
export function useOverlayLayer() {
    const context = use(ThemeContext);
    return context === null ? undefined : context.layer;
}
/** The reading direction, or `rtl` (the product default) outside a provider. */
export function useDirection() {
    return use(ThemeContext)?.direction ?? 'rtl';
}
//# sourceMappingURL=ThemeProvider.js.map